import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import {
  RootState,
  getCreditLineCheckoutSummary,
  isCreditLineCheckoutAccount,
  renderNumber,
  getCurrencyText,
} from '@share/utils';

import './style.scss';

export type CreditLineCheckoutSummaryProps = {
  /** Total amount this booking will charge to the credit line (booking currency). */
  bookingAmount: number;
  currency?: string;
};

const TWO = 2;

export function CreditLineCheckoutSummary({ bookingAmount, currency }: CreditLineCheckoutSummaryProps) {
  const { account, creditLine, creditLineLoading } = useSelector(
    (state: RootState) => state.loginStore,
  );

  const summary = useMemo(
    () => getCreditLineCheckoutSummary(creditLine, bookingAmount),
    [creditLine, bookingAmount],
  );

  if (!isCreditLineCheckoutAccount(account)) {
    return null;
  }

  if (creditLineLoading && !summary) {
    return (
      <div className="credit-line-checkout-summary credit-line-checkout-summary--loading">
        <FormattedMessage id="credit_line.checkout.loading" defaultMessage="Loading credit line…" />
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const currencyLabel = currency ? getCurrencyText(currency) : '';
  const formatAmount = (amount: number) => `${currencyLabel}${renderNumber(amount, TWO)}`;

  const statusClass =
    summary.status === 'insufficient'
      ? 'insufficient'
      : summary.status === 'low'
        ? 'low'
        : 'sufficient';

  const badgeId =
    summary.status === 'insufficient'
      ? 'credit_line.checkout.status.insufficient'
      : summary.status === 'low'
        ? 'credit_line.checkout.status.low'
        : 'credit_line.checkout.status.sufficient';

  const remainingClass =
    summary.remaining < 0
      ? 'credit-line-checkout-summary__value--remaining-negative'
      : 'credit-line-checkout-summary__value--remaining-positive';

  return (
    <div className={`credit-line-checkout-summary credit-line-checkout-summary--${statusClass}`}>
      <div className="credit-line-checkout-summary__header">
        <div className="credit-line-checkout-summary__icon" aria-hidden>
          <FontAwesomeIcon icon={faCreditCard} />
        </div>
        <div>
          <div className="credit-line-checkout-summary__title-row">
            <h3 className="credit-line-checkout-summary__title">
              <FormattedMessage id="credit_line.checkout.title" defaultMessage="Credit line" />
            </h3>
            <span
              className={`credit-line-checkout-summary__badge credit-line-checkout-summary__badge--${statusClass}`}
            >
              <FormattedMessage id={badgeId} />
            </span>
            {summary.showLowRemainingWarning ? (
              <Tooltip
                title={
                  <FormattedMessage
                    id="credit_line.checkout.low_remaining_tooltip"
                    defaultMessage="Remaining balance is below 10% of your available credit line."
                  />
                }
              >
                <span className="credit-line-checkout-summary__warning-icon" role="img" aria-label="warning">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </span>
              </Tooltip>
            ) : null}
          </div>
          <p className="credit-line-checkout-summary__subtitle">
            <FormattedMessage
              id="credit_line.checkout.subtitle"
              defaultMessage="This booking will be charged to your credit line"
            />
          </p>
        </div>
      </div>

      <div className="credit-line-checkout-summary__math">
        <div className="credit-line-checkout-summary__column">
          <span className="credit-line-checkout-summary__label">
            <FormattedMessage id="credit_line.checkout.available" defaultMessage="Available" />
          </span>
          <span className="credit-line-checkout-summary__value">{formatAmount(summary.available)}</span>
        </div>
        <span className="credit-line-checkout-summary__operator" aria-hidden>
          −
        </span>
        <div className="credit-line-checkout-summary__column">
          <span className="credit-line-checkout-summary__label">
            <FormattedMessage id="credit_line.checkout.this_booking" defaultMessage="This booking" />
          </span>
          <span className="credit-line-checkout-summary__value">{formatAmount(summary.bookingAmount)}</span>
        </div>
        <span className="credit-line-checkout-summary__operator" aria-hidden>
          =
        </span>
        <span className="credit-line-checkout-summary__divider" aria-hidden />
        <div className="credit-line-checkout-summary__column">
          <span className="credit-line-checkout-summary__label">
            <FormattedMessage id="credit_line.checkout.remaining_after" defaultMessage="Remaining after" />
          </span>
          <span className={`credit-line-checkout-summary__value ${remainingClass}`}>
            {formatAmount(summary.remaining)}
          </span>
        </div>
      </div>
    </div>
  );
}
